import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Client, Message } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { environment } from '../../environments/environment';

export interface Notification {
  id: string;
  type: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private client!: Client;
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private individualNotificationSubject = new BehaviorSubject<Notification | null>(null);
  private isConnected = false;

  constructor() {
    // Delay WebSocket initialization to ensure config.js is loaded
    setTimeout(() => {
      this.initializeWebSocketConnection();
    }, 100);

    // Ensure we close the socket cleanly when the tab is closed or refreshed
    window.addEventListener('beforeunload', () => this.disconnect());
  }

  private initializeWebSocketConnection(): void {
    // Get API URL dynamically each time
    const apiUrl = environment.apiUrl;
    const wsUrl = `${apiUrl.replace('/api', '')}/ws`;
    console.log('🔌 [WEBSOCKET] Initializing connection to:', wsUrl);

    this.client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      debug: (str) => {
        // Debug logging
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000
    });

    this.client.onConnect = (frame) => {
      this.isConnected = true;
      this.subscribeToNotifications();
    };

    this.client.onStompError = (frame) => {
      this.isConnected = false;
    };

    this.client.onDisconnect = () => {
      this.isConnected = false;
      this.activeSubscriptions.clear(); // Clear local tracking on disconnect
    };

    this.client.activate();
  }

  private activeSubscriptions = new Map<string, any>();

  /**
   * Universal method to subscribe to any STOMP topic.
   */
  subscribeToTopic(topic: string, callback: (message: any) => void): void {
    const attemptSubscription = () => {
      if (this.isConnected && !this.activeSubscriptions.has(topic)) {
        console.log(`📡 [WEBSOCKET] Subscribing to: ${topic}`);
        const subscription = this.client.subscribe(topic, (message: Message) => {
          callback(message.body);
        });
        this.activeSubscriptions.set(topic, subscription);
      } else if (!this.isConnected) {
        // If not connected, wait a bit and retry
        setTimeout(() => attemptSubscription(), 1000);
      }
    };

    attemptSubscription();
  }

  private subscribeToNotifications(): void {
    if (!this.isConnected) {
      return;
    }

    // Subscribe to individual user notifications
    this.client.subscribe('/user/queue/notifications', (message: Message) => {
      try {
        const notification: Notification = JSON.parse(message.body);
        this.addNotification(notification);
      } catch (error) {
        // Handle parsing error silently
      }
    });

    // Subscribe to admin notifications
    this.client.subscribe('/topic/admin/notifications', (message: Message) => {
      try {
        const notification: Notification = JSON.parse(message.body);

        // Only add admin notifications if current user is admin
        const user = this.getCurrentUser();

        if (user && user.role && user.role.toUpperCase() === 'ADMIN') {
          this.addNotification(notification);
        }
      } catch (error) {
        // Handle parsing error silently
      }
    });
  }

  private addNotification(notification: Notification): void {
    // Emit individual notification for real-time updates
    this.individualNotificationSubject.next(notification);

    // Also update the full list for backward compatibility
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];
    this.notificationsSubject.next(updatedNotifications);
  }

  getNotifications(): Observable<Notification[]> {
    return this.notificationsSubject.asObservable();
  }

  getIndividualNotifications(): Observable<Notification | null> {
    return this.individualNotificationSubject.asObservable();
  }

  getUnreadCount(): Observable<number> {
    return new Observable(observer => {
      this.notificationsSubject.subscribe(notifications => {
        const unreadCount = notifications.filter(n => !n.read).length;
        observer.next(unreadCount);
      });
    });
  }

  markAsRead(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification =>
      notification.id === notificationId ? { ...notification, read: true } : notification
    );
    this.notificationsSubject.next(updatedNotifications);
  }

  markAllAsRead(): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => ({
      ...notification,
      read: true
    }));
    this.notificationsSubject.next(updatedNotifications);
  }

  clearNotifications(): void {
    this.notificationsSubject.next([]);
  }

  isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  disconnect(): void {
    if (this.client) {
      this.client.deactivate();
    }
  }

  setNotificationsFromBackend(notifications: Notification[]): void {
    this.notificationsSubject.next(notifications);
  }

  // Method to refresh admin subscription when user data changes
  refreshAdminSubscription(): void {
    if (this.isConnected) {
      // The subscription is already active
      const user = this.getCurrentUser();
    }
  }

  private getCurrentUser(): any {
    // Get user from localStorage or sessionStorage
    const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        return null;
      }
    }
    return null;
  }
} 